import React from 'react';
import { Search, Filter, X, Calendar, Settings, Target, User, Activity, Clock, Gauge, ThumbsUp, ThumbsDown } from 'lucide-react';
import { DateRangePicker } from './date-range-picker';
import { MultiSelect } from './multi-select';
import { StatusFilter } from './StatusFilter';
import { Input } from './input';
import { Button } from './button';

export interface PassViewFiltersProps { 

  passNameFilter: string;
  onPassNameFilterChange: (value: string) => void;
  stationFilter: string;
  onStationFilterChange: (value: string) => void;
  bugTypeFilter: string[];
  onBugTypeFilterChange: (value: string[]) => void;
  statusFilter: string[];
  onStatusFilterChange: (value: string[]) => void;
  welderIdFilter: string[];
  onWelderIdFilterChange: (value: string[]) => void; 
  dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  onDateRangeChange: (value: { dateFrom: Date | undefined; dateTo: Date | undefined }) => void;
   
  bugTypeOptions: string[];
  statusOptions: string[];
  welderIdOptions: string[];
  stationOptions: string[];
  
  // New props for count display
  totalCount: number;
  filteredCount: number;
  
  // Mobile flag
  isMobile?: boolean;

  /** Optional display label for bug type filter options/chips (values unchanged). */
  getBugTypeLabel?: (value: string) => string;
}

export const PassViewFilters: React.FC<PassViewFiltersProps> = ({
  passNameFilter,
  onPassNameFilterChange,
  stationFilter,
  onStationFilterChange,
  bugTypeFilter,
  onBugTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  welderIdFilter,
  onWelderIdFilterChange,
  dateRange,
  onDateRangeChange,
  bugTypeOptions,
  statusOptions,
  welderIdOptions,
  stationOptions,
  totalCount,
  filteredCount,
  isMobile = false,
  getBugTypeLabel,
}) => {
  const formatBugTypeLabel = (value: string) => getBugTypeLabel?.(value) ?? value;

  const clearAllFilters = () => {
    onPassNameFilterChange('');
    onStationFilterChange('');
    onBugTypeFilterChange([]);
    onStatusFilterChange([]);
    onWelderIdFilterChange([]);
    onDateRangeChange({ dateFrom: undefined, dateTo: undefined });
  };

  const hasActiveFilters = passNameFilter || stationFilter || bugTypeFilter.length > 0 || statusFilter.length > 0 || 
    welderIdFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${isMobile ? 'p-2 mb-2' : 'p-4 mb-4'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Filter className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500`} />
          <span className={`font-medium text-gray-700 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>Pass View Filters</span>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className={`text-gray-500 hover:text-gray-700 ${isMobile ? 'h-7 px-2' : ''}`}
          >
            <X className={`${isMobile ? 'h-3 w-3 mr-0.5' : 'h-4 w-4 mr-1'}`} />
            <span className={isMobile ? 'text-[10px]' : ''}>Clear All</span>
          </Button>
        )}
      </div>

      {/* Filters Grid */}
      <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-5 gap-2'}`}>
        {/* Pass Name Filter */}
        <div className={isMobile ? 'space-y-1' : 'space-y-2'}>
          <label className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-700 flex items-center gap-1`}>
            <Target className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            Pass Name
          </label>
          <Input
            placeholder="Search pass name..."
            value={passNameFilter}
            onChange={(e) => onPassNameFilterChange(e.target.value)}
            className={`w-full ${isMobile ? 'h-8 text-xs' : ''}`}
          />
        </div>

        {/* Station Filter */}
        <div className={isMobile ? 'space-y-1' : 'space-y-2'}>
          <label className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-700 flex items-center gap-1`}>
            <Target className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            Station
          </label>
          <Input
            placeholder="Search station..."
            value={stationFilter}
            onChange={(e) => onStationFilterChange(e.target.value)}
            className={`w-full ${isMobile ? 'h-8 text-xs' : ''}`}
          />
        </div>

        {/* Bug Type Filter */}
        <div className={isMobile ? 'space-y-1' : 'space-y-2'}>
          <label className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-700 flex items-center gap-1`}>
            <Settings className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            Bug Type
          </label>
          <MultiSelect
            options={bugTypeOptions.map((option) => ({
              value: option,
              label: formatBugTypeLabel(option),
            }))}
            selected={bugTypeFilter}
            onChange={onBugTypeFilterChange}
            placeholder="Select bug types"
          />
        </div>

        {/* Status Filter */}
        <div className={isMobile ? 'space-y-1' : 'space-y-2'}>
          <label className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-700 flex items-center gap-1`}>
            <Activity className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            Status
          </label>
          <StatusFilter
            selected={statusFilter}
            onChange={onStatusFilterChange}
            placeholder="Select status"
          />
        </div>

  
        {/* Date Range Filter */}
        <div className={isMobile ? 'space-y-1' : 'space-y-2'}>
          <label className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-700 flex items-center gap-1`}>
            <Calendar className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            Date Range
          </label>
          
          <DateRangePicker
            initialDateFrom={dateRange.dateFrom}
            initialDateTo={dateRange.dateTo}
            onUpdate={(values) => onDateRangeChange({
              dateFrom: values.dateFrom,
              dateTo: values.dateTo
            })}
            showCompare={false} 
            className="w-full"
          />
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className={`${isMobile ? 'mt-2 pt-2' : 'mt-4 pt-4'} border-t border-gray-200`}>
          <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
            {passNameFilter && (
              <span className={`inline-flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full`}>
                Pass: {passNameFilter}
                <button
                  onClick={() => onPassNameFilterChange('')}
                  className="ml-0.5 hover:text-blue-600"
                >
                  <X className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                </button>
              </span>
            )}
            {stationFilter && (
              <span className={`inline-flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} bg-cyan-100 text-cyan-800 rounded-full`}>
                Station: {stationFilter}
                <button
                  onClick={() => onStationFilterChange('')}
                  className="ml-0.5 hover:text-cyan-600"
                >
                  <X className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                </button>
              </span>
            )}
            {bugTypeFilter.length > 0 && (
              <span className={`inline-flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} text-galvanizing-green bg-[rgba(31,154,58,0.08)] rounded-full`}>
                Bug: {bugTypeFilter.map(formatBugTypeLabel).join(', ')}
                <button
                  onClick={() => onBugTypeFilterChange([])}
                  className="ml-0.5 hover:text-green-600"
                >
                  <X className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                </button>
              </span>
            )}
            {statusFilter.length > 0 && (
              <span className={`inline-flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full`}>
                Status: {statusFilter.join(', ')}
                <button
                  onClick={() => onStatusFilterChange([])}
                  className="ml-0.5 hover:text-purple-600"
                >
                  <X className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                </button>
              </span>
            )}
            {welderIdFilter.length > 0 && (
              <span className={`inline-flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} text-[#B38600] bg-[rgba(230,161,0,0.08)] rounded-full`}>
                Welder: {welderIdFilter.join(', ')}
                <button
                  onClick={() => onWelderIdFilterChange([])}
                  className="ml-0.5 hover:text-orange-600"
                >
                  <X className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                </button>
              </span>
            )}
            {(dateRange.dateFrom || dateRange.dateTo) && (
              <span className={`inline-flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full`}>
                {isMobile ? 'Date' : 'Date Range'}: {dateRange.dateFrom ? `${dateRange.dateFrom.getDate().toString().padStart(2, '0')}-${(dateRange.dateFrom.getMonth() + 1).toString().padStart(2, '0')}-${dateRange.dateFrom.getFullYear()} ${dateRange.dateFrom.getHours() % 12 || 12}:${dateRange.dateFrom.getMinutes().toString().padStart(2, '0')} ${dateRange.dateFrom.getHours() >= 12 ? 'PM' : 'AM'}` : ''} - {dateRange.dateTo ? `${dateRange.dateTo.getDate().toString().padStart(2, '0')}-${(dateRange.dateTo.getMonth() + 1).toString().padStart(2, '0')}-${dateRange.dateTo.getFullYear()} ${dateRange.dateTo.getHours() % 12 || 12}:${dateRange.dateTo.getMinutes().toString().padStart(2, '0')} ${dateRange.dateTo.getHours() >= 12 ? 'PM' : 'AM'}` : ''}
                <button
                  onClick={() => onDateRangeChange({ dateFrom: undefined, dateTo: undefined })}
                  className="ml-0.5 hover:text-indigo-600"
                >
                  <X className={isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pagination Display */}
      <div className={`${isMobile ? 'mt-2 pt-2' : 'mt-4 pt-4'} border-t border-gray-200`}>
        <div className="flex items-center justify-end">
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-2'}`}>
            <span className={`${isMobile ? 'text-[10px]' : 'text-sm'} font-medium text-gray-700`}>Showing:</span>
            <span className={`${isMobile ? 'text-[10px]' : 'text-sm'} text-gray-900 font-semibold`}>
              {filteredCount > 0 ? `1-${filteredCount}` : '0'} of {totalCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};  